import { useCallback, useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ActivityStrip } from './ActivityStrip';
import { ChatSplitter } from './ChatSplitter';
import { CommandPalette } from './CommandPalette';
import { LeftNav } from './LeftNav';
import { ShortcutHelpDialog } from './ShortcutHelpDialog';
import { TopBar } from './TopBar';
import { Workspace } from './Workspace';
import { ChatPane } from '@/chat/ChatPane';
import { MobileChatDrawer } from '@/chat/MobileChatDrawer';
import { requestFocusChatComposer } from '@/chat/Composer';
import { CHAT_MIN_HEIGHT, CHAT_MIN_WIDTH, useAppStore } from './appStore';
import { useShellShortcuts } from './useGlobalShortcuts';
import { WorkspaceLayoutDialogs } from '@/templates/WorkspaceLayoutDialogs';
import { panelMaximizeBus } from '@/panels/panelMaximizeBus';
import { usePanelStore } from '@/panels/panelStore';
import {
  dismissAutosave,
  hasAutosaveSnapshot,
  installAutosave,
  readAutosaveSnapshot,
  restoreAutosave,
} from '@/panels/autosave';
import { useIsMobile } from '@/shared/useIsMobile';
import { UiPreferencesBridge } from './UiPreferencesBridge';

export function Shell() {
  const chatPosition = useAppStore((s) => s.chatPosition);
  const chatCollapsed = useAppStore((s) => s.chatCollapsed);
  const chatWidth = useAppStore((s) => s.chatWidth);
  const chatHeight = useAppStore((s) => s.chatHeight);
  const setChatWidth = useAppStore((s) => s.setChatWidth);
  const setChatHeight = useAppStore((s) => s.setChatHeight);
  const toggleChat = useAppStore((s) => s.toggleChat);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [leftNavMobileOpen, setLeftNavMobileOpen] = useState(false);

  const isMobile = useIsMobile();
  const focusedPanelId = usePanelStore((s) => s.focusedPanelId);
  const panels = usePanelStore((s) => s.panels);
  const { t: tCommon } = useTranslation('common');

  // Autosave (issue 187): install the persistence subscription once, and
  // surface a recovery banner if a previous-session snapshot exists.
  const [recovery, setRecovery] = useState<{ count: number } | null>(null);
  useEffect(() => {
    const teardown = installAutosave();
    // Decide whether to offer recovery based on the boot-time workspace
    // emptiness — never auto-restore.
    const bootPanels = usePanelStore.getState().panels.filter((p) => !p.detached);
    if (bootPanels.length === 0 && hasAutosaveSnapshot()) {
      const snapshot = readAutosaveSnapshot();
      setRecovery({ count: snapshot?.tiles.length ?? 0 });
    }
    return teardown;
  }, []);

  useEffect(() => {
    if (!recovery) return;
    const timer = window.setTimeout(() => setRecovery(null), 30_000);
    return () => window.clearTimeout(timer);
  }, [recovery]);

  const handleRestoreAutosave = useCallback(() => {
    restoreAutosave();
    setRecovery(null);
  }, []);

  const handleDismissAutosave = useCallback(() => {
    dismissAutosave();
    setRecovery(null);
  }, []);

  const focusChatComposer = useCallback(() => {
    if (chatCollapsed) toggleChat();
    requestFocusChatComposer();
  }, [chatCollapsed, toggleChat]);

  const maximizeFocusedPanel = useCallback(() => {
    const targetId =
      focusedPanelId ?? [...panels].reverse().find((p) => !p.detached)?.panelId;
    if (targetId) panelMaximizeBus.dispatch(targetId);
  }, [focusedPanelId, panels]);

  useShellShortcuts({
    openShortcutHelp: () => setShortcutHelpOpen(true),
    focusChatComposer,
    maximizeFocusedPanel,
  });

  const chatVisible = !chatCollapsed && chatPosition !== 'detached';

  return (
    <div
      className="flex h-screen flex-col bg-background text-foreground"
      data-chat-position={chatPosition}
    >
      <UiPreferencesBridge />
      <a
        href="#main-workspace"
        data-testid="skip-to-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[100] focus:rounded focus:bg-[color:var(--wb-accent)] focus:px-3 focus:py-1 focus:text-white focus:outline-none"
      >
        {tCommon('skipToMain', { defaultValue: 'Skip to main content' })}
      </a>
      <TopBar
        onMobileMenuOpen={() => setLeftNavMobileOpen(true)}
      />
      {recovery ? (
        <div
          role="status"
          aria-live="polite"
          data-testid="autosave-recovery-banner"
          className="flex items-center gap-2 border-b border-border bg-muted px-3 py-1.5 text-sm"
        >
          <History className="h-4 w-4 text-[color:var(--wb-accent)]" aria-hidden="true" />
          <span className="text-foreground">
            {tCommon('autosave.recoveryPrompt', {
              count: recovery.count,
              defaultValue: 'Restore {{count}} surfaces from your last session?',
            })}
          </span>
          <button
            type="button"
            onClick={handleRestoreAutosave}
            data-testid="autosave-restore-btn"
            className="rounded border border-border bg-card px-2 py-1 text-xs hover:bg-background"
          >
            {tCommon('autosave.restore', { defaultValue: 'Restore' })}
          </button>
          <button
            type="button"
            onClick={handleDismissAutosave}
            data-testid="autosave-dismiss-btn"
            className="text-muted-foreground hover:text-foreground rounded px-2 py-1 text-xs"
          >
            {tCommon('autosave.dismiss', { defaultValue: 'Dismiss' })}
          </button>
        </div>
      ) : null}
      {/* Desktop docked layout: LeftNav | workspace column | chat right-rail */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* LeftNav — docked flex child on desktop; overlay drawer on mobile */}
        <LeftNav
          mobileOpen={leftNavMobileOpen}
          onClose={() => setLeftNavMobileOpen(false)}
        />

        {/* Center column: workspace + optional footer chat stacked vertically */}
        <main
          id="main-workspace"
          className="relative flex min-h-0 min-w-0 flex-1 flex-col"
        >
          <Workspace />

          {!isMobile && chatVisible && chatPosition === 'footer' && (
            <div
              className="flex shrink-0 flex-col overflow-hidden border-t border-border bg-card"
              style={{ height: chatHeight + 4 }}
            >
              <ChatSplitter
                orientation="vertical"
                size={chatHeight}
                onResize={setChatHeight}
                min={CHAT_MIN_HEIGHT}
              />
              <div
                className="flex w-full shrink-0 flex-col"
                style={{ height: chatHeight }}
                data-testid="chat-dock-footer"
              >
                <ChatPane />
              </div>
            </div>
          )}
        </main>

        {/* Chat right-rail — docked flex child */}
        {!isMobile && chatVisible && chatPosition === 'right' && (
          <div
            className="flex shrink-0 overflow-hidden border-l border-border bg-card"
            style={{ width: chatWidth + 4 }}
            data-testid="chat-dock-right"
          >
            <ChatSplitter
              orientation="horizontal"
              size={chatWidth}
              onResize={setChatWidth}
              min={CHAT_MIN_WIDTH}
            />
            <div
              className="flex h-full shrink-0 flex-col"
              style={{ width: chatWidth }}
            >
              <ChatPane />
            </div>
          </div>
        )}
      </div>

      {/* Mobile bottom drawer chat */}
      {isMobile && <MobileChatDrawer />}

      <ActivityStrip />
      <CommandPalette />
      <ShortcutHelpDialog open={shortcutHelpOpen} onOpenChange={setShortcutHelpOpen} />
      <WorkspaceLayoutDialogs />
    </div>
  );
}
